<template>
  <component
    :is="tag"
    :to="to"
    :href="href"
    :type="tag === 'button' ? type : undefined"
    :disabled="isDisabled"
    :aria-busy="loading ? 'true' : undefined"
    class="adm-btn"
    :class="[`adm-btn--${variant}`, `adm-btn--${size}`, { 'adm-btn--block': block }]"
  >
    <Loader2 v-if="loading" :size="iconSize" class="adm-btn__spinner" />
    <component v-else-if="icon" :is="icon" :size="iconSize" />
    <span v-if="$slots.default" class="adm-btn__label"><slot /></span>
  </component>
</template>

<script>
/**
 * Botón del panel — plan 27_09_2026 §4.2 (criterio C6).
 *
 * Por qué existe: las cuatro vistas de Print3DAdmin redefinían `.button` en
 * su `<style scoped>` (fondo blanco, radio 6px), y por especificidad ganaban
 * al botón corporativo de `global.css`. El panel tenía dos botones distintos
 * según en qué pantalla estuvieras. Aquí hay uno solo, construido sobre los
 * mismos tokens de acción de la marca que usa la web pública.
 *
 * Renderiza `<button>`, `<router-link>` o `<a>` según se pase `to` o `href`,
 * para que un enlace de navegación no tenga que fingir ser un botón.
 */
import { computed } from 'vue';
import { Loader2 } from 'lucide-vue-next';

export default {
  name: 'AdmButton',
  components: { Loader2 },
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (v) => ['primary', 'secondary', 'ghost', 'danger'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md'].includes(v),
    },
    type: { type: String, default: 'button' },
    /** Destino de router-link. Excluyente con `href`. */
    to: { type: [String, Object], default: null },
    href: { type: String, default: null },
    icon: { type: [Object, Function], default: null },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
  },
  setup(props) {
    const tag = computed(() => {
      if (props.to) return 'router-link';
      if (props.href) return 'a';
      return 'button';
    });

    // Un router-link/<a> deshabilitado no existe en HTML; en ese caso el
    // atributo se omite y basta con no pasar el enlace.
    const isDisabled = computed(() =>
      tag.value === 'button' ? props.disabled || props.loading : undefined,
    );

    const iconSize = computed(() => (props.size === 'sm' ? 15 : 17));

    return { tag, isDisabled, iconSize, Loader2 };
  },
};
</script>

<style scoped>
.adm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0 0.95rem;
  height: var(--adm-control-height);
  border: 1px solid transparent;
  border-radius: var(--adm-radius-sm);
  box-sizing: border-box;
  font-family: var(--font-body);
  font-size: var(--adm-font-base);
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.adm-btn--sm {
  height: 30px;
  padding: 0 0.6rem;
  font-size: var(--adm-font-sm);
}

.adm-btn--block { width: 100%; }

.adm-btn:disabled { opacity: 0.55; cursor: not-allowed; }

/* --- Variantes ---
   El primario es el azul acero de acción de la marca (--color-action),
   el mismo de los CTA públicos. */
.adm-btn--primary {
  background-color: var(--color-action);
  border-color: var(--color-action);
  color: var(--color-white);
}
.adm-btn--primary:hover:not(:disabled) {
  background-color: var(--color-action-hover);
  border-color: var(--color-action-hover);
}

.adm-btn--secondary {
  background-color: var(--adm-surface);
  border-color: var(--adm-border-strong);
  color: var(--color-text-strong);
}
.adm-btn--secondary:hover:not(:disabled) {
  border-color: var(--color-action);
  color: var(--color-action);
}

.adm-btn--ghost {
  background: none;
  border-color: transparent;
  color: var(--color-text);
}
.adm-btn--ghost:hover:not(:disabled) {
  background-color: var(--adm-surface-alt);
  color: var(--color-title);
}

.adm-btn--danger {
  background-color: var(--adm-surface);
  border-color: var(--adm-danger-fg);
  color: var(--adm-danger-fg);
}
.adm-btn--danger:hover:not(:disabled) {
  background-color: var(--adm-danger-fg);
  color: var(--color-white);
}

.adm-btn__spinner { animation: adm-spin 0.9s linear infinite; }

@keyframes adm-spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .adm-btn__spinner { animation-duration: 2.5s; }
}
</style>
